import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ConfirmCreditsModal } from '../credits/ConfirmCreditsModal';
import { useSellerAutomation } from '../../hooks/useSellerAutomation';

// ---------------------------------------------------------------------------
// Pecas do painel "Automacoes" do Seller. Antes cada aba de configuracao tinha
// o seu jeito: switch que salvava na hora, checkbox que dependia de "Salvar",
// intervalo digitado em horas ("168 = uma vez por semana"). Aqui e um padrao so:
//   - tudo salva sozinho (useAutoSave + SaveStatus)
//   - toda rotina e uma AutomationRow: interruptor + frequencia + ultima/proxima
//   - regra numerica e um RuleField com rotulo em portugues e a sigla ao lado
//   - o que quase ninguem mexe fica em <Advanced>, recolhido
// ---------------------------------------------------------------------------

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Salva com debounce, mandando so os campos alterados (o backend faz merge com o
 * que ja esta salvo). Patches seguidos se acumulam ate o envio. `save` pode
 * devolver `{ success: false, message }` ou lancar; os dois viram erro.
 * `schedule(patch, true)` salva na hora (interruptores).
 */
export function useAutoSave<T extends object>(save: (patch: Partial<T>) => Promise<any>) {
  const [status, setStatus] = useState<SaveState>('idle');
  const timer = useRef<number>();
  const pending = useRef<Partial<T> | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  const flush = useCallback(async () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    const patch = pending.current;
    pending.current = null;
    if (!patch) return;
    setStatus('saving');
    try {
      const res = await saveRef.current(patch);
      if (res && res.success === false) throw new Error(res.message || 'Falha ao salvar');
      setStatus('saved');
    } catch (err: any) {
      setStatus('error');
      toast.error(err?.message || 'Falha ao salvar');
    }
  }, []);

  const schedule = useCallback((patch: Partial<T>, immediate = false) => {
    pending.current = { ...(pending.current || {}), ...patch };
    window.clearTimeout(timer.current);
    if (immediate) flush();
    else timer.current = window.setTimeout(flush, 700);
  }, [flush]);

  // Fechou o painel no meio da digitacao: nao perde a ultima mudanca.
  useEffect(() => () => { if (timer.current) flush(); }, [flush]);

  return { status, schedule };
}

export function SaveStatus({ status }: { status: SaveState }) {
  const map: Record<SaveState, { text: string; cls: string }> = {
    idle: { text: 'As mudanças salvam sozinhas', cls: 'text-gray-400' },
    saving: { text: 'Salvando…', cls: 'text-gray-500' },
    saved: { text: 'Salvo ✓', cls: 'text-emerald-600' },
    error: { text: 'Não salvou. Tente de novo.', cls: 'text-red-600' },
  };
  const m = map[status];
  return <span className={`text-[11px] font-medium ${m.cls}`}>{m.text}</span>;
}

export function SettingsSection({ title, help, right, children }: { title: string; help?: ReactNode; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          {help && <p className="mt-0.5 text-xs text-gray-500">{help}</p>}
        </div>
        {right}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? 'bg-emerald-600' : 'bg-gray-300'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const FREQUENCIES = [
  { hours: 4, label: 'a cada 4 horas' },
  { hours: 12, label: 'a cada 12 horas' },
  { hours: 24, label: '1 vez por dia' },
  { hours: 72, label: 'a cada 3 dias' },
  { hours: 168, label: '1 vez por semana' },
];

export function frequencyLabel(hours: number | null | undefined) {
  const h = hours && hours > 0 ? hours : 24;
  return FREQUENCIES.find((f) => f.hours === h)?.label || `a cada ${h} horas`;
}

function formatWhen(value: string | null | undefined) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/**
 * Uma rotina automatica. `automationKey` busca ultima/proxima execucao no
 * status central. `credit` faz o interruptor pedir confirmacao de custo ao ligar.
 */
export function AutomationRow({
  title, help, enabled, onToggle, hours, onHoursChange, fixedFrequency, automationKey, credit, children,
}: {
  title: string;
  help: ReactNode;
  enabled: boolean;
  onToggle: (next: boolean) => void | Promise<void>;
  hours?: number | null;
  onHoursChange?: (hours: number) => void;
  /** Frequencia que o usuario nao escolhe (ex.: "1 vez por dia"). */
  fixedFrequency?: string;
  automationKey?: string;
  credit?: { featureKey: string; unitLabel: string; estimate: () => Promise<number> };
  children?: ReactNode;
}) {
  const { byKey } = useSellerAutomation();
  const status = automationKey ? byKey(automationKey) : null;
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState<number | null>(null);

  const toggle = async (next: boolean) => {
    if (next && credit) {
      setBusy(true);
      try { const n = await credit.estimate(); setQty(Number.isFinite(n) ? n : 0); }
      catch { toast.error('Não foi possível estimar o custo. Tente novamente.'); }
      finally { setBusy(false); }
      return;
    }
    setBusy(true);
    try { await onToggle(next); } finally { setBusy(false); }
  };

  const last = formatWhen(status?.lastRunAt);
  const next = enabled ? formatWhen(status?.nextRunAt) : null;
  const options = hours && !FREQUENCIES.some((f) => f.hours === hours) ? [...FREQUENCIES, { hours, label: `a cada ${hours} horas` }] : FREQUENCIES;

  return (
    <div className={`rounded-lg border p-3.5 transition ${enabled ? 'border-emerald-200 bg-emerald-50/40' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <p className="mt-0.5 text-xs text-gray-500">{help}</p>
        </div>
        <Switch checked={enabled} onChange={toggle} disabled={busy} label={title} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
        {onHoursChange ? (
          <select
            value={hours || 24}
            onChange={(e) => onHoursChange(Number(e.target.value))}
            disabled={!enabled}
            className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 disabled:opacity-60"
          >
            {options.map((f) => <option key={f.hours} value={f.hours}>{f.label}</option>)}
          </select>
        ) : fixedFrequency ? (
          <span className="font-medium text-gray-600">{fixedFrequency}</span>
        ) : null}
        {last && <span>Última: {last}</span>}
        {next && <span>Próxima: {next}</span>}
        {credit && <span className="text-amber-700">Consome créditos a cada execução</span>}
      </div>
      {children && enabled && <div className="mt-3 border-t border-emerald-100 pt-3">{children}</div>}

      {credit && (
        <ConfirmCreditsModal
          open={qty !== null}
          title={`Ligar: ${title}`}
          items={qty !== null ? [{ featureKey: credit.featureKey, qty, label: credit.unitLabel }] : []}
          recurringHours={hours && hours > 0 ? hours : 24}
          note="Você só paga pelos itens analisados de fato em cada execução (itens sem dados ou pausados não são cobrados)."
          confirmLabel="Ligar automação"
          onConfirm={async () => { setQty(null); setBusy(true); try { await onToggle(true); } finally { setBusy(false); } }}
          onCancel={() => setQty(null)}
        />
      )}
    </div>
  );
}

/** Campo numerico com rotulo em portugues, sigla tecnica ao lado e valor padrao visivel. */
export function RuleField({
  label, tech, help, value, onChange, suffix, defaultValue, allowEmpty, emptyLabel = 'automático', integer,
}: {
  label: string;
  tech?: string;
  help?: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  suffix?: string;
  defaultValue?: number | null;
  allowEmpty?: boolean;
  emptyLabel?: string;
  integer?: boolean;
}) {
  // Texto proprio para nao perder a digitacao intermediaria ("4," / "4.").
  const [text, setText] = useState<string>(value != null ? String(value) : '');
  useEffect(() => {
    if (value == null) { if (text !== '') setText(''); return; }
    if (Number(text.replace(',', '.')) !== value) setText(String(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <label className="block">
      <span className="flex flex-wrap items-baseline gap-1.5">
        <span className="text-xs font-medium text-gray-800">{label}</span>
        {tech && <span className="rounded bg-gray-100 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-gray-500">{tech}</span>}
      </span>
      <span className="mt-1 flex items-center gap-1.5">
        <input
          type="text"
          inputMode="decimal"
          value={text}
          placeholder={allowEmpty ? emptyLabel : ''}
          onChange={(e) => {
            let t = e.target.value.replace(/[^0-9.,]/g, '');
            const sep = t.search(/[.,]/);
            if (sep !== -1) t = integer ? t.slice(0, sep) : t.slice(0, sep + 1) + t.slice(sep + 1).replace(/[.,]/g, '');
            setText(t);
            if (t === '' || t === '.' || t === ',') { if (allowEmpty) onChange(null); return; }
            const n = Number(t.replace(',', '.'));
            if (!Number.isNaN(n)) onChange(integer ? Math.round(n) : n);
          }}
          onBlur={() => setText(value != null ? String(value) : '')}
          className="w-28 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-300"
        />
        {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
        {defaultValue !== undefined && value !== defaultValue && (
          <button type="button" onClick={() => onChange(defaultValue)} className="ml-1 text-[10px] text-gray-400 hover:text-orange-700 hover:underline">
            voltar ao padrão ({defaultValue == null ? emptyLabel : `${defaultValue} ${suffix || ''}`.trim()})
          </button>
        )}
      </span>
      {help && <span className="mt-1 block text-[11px] leading-snug text-gray-500">{help}</span>}
    </label>
  );
}

export function Advanced({ title = 'Avançado', help, children }: { title?: string; help?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-xl border border-dashed border-gray-300">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span>
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {help && <span className="block text-[11px] text-gray-500">{help}</span>}
        </span>
        <span className="text-xs text-gray-400">{open ? 'ocultar' : 'mostrar'}</span>
      </button>
      {open && <div className="space-y-4 border-t border-dashed border-gray-300 px-4 py-4">{children}</div>}
    </section>
  );
}
